import React, { useState, useEffect } from "react";
import { StyleSheet, View } from "react-native";
import { useAuth } from "@/hooks/auth";
import { getUserFacts } from "@/store";
import { UserFacts } from "@/store/types";
import { brand, layout } from "@/constants";
import {
  ScreenLayout,
  ScrollView,
  Spinner,
  Title,
  Text,
  FactCard,
  NotificationCard,
  DisclaimerFooter
} from "@/components";
import { logger } from "@/utils/logger";
import { showToast } from "@/utils/toast";
import * as apiClient from "@/utils/serenity-api-client";

/**
 * Facts screen component
 * Displays user facts as individual cards
 */
export default function FactsScreen() {
  const { user, getIdToken } = useAuth();
  const [facts, setFacts] = useState<UserFacts | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user?.uid) return;

    const loadFacts = async () => {
      try {
        setLoading(true);
        const data = await getUserFacts(user.uid);
        setFacts(data);
        setError(null);
      } catch (err) {
        logger.error("Failed to load facts:", err);
        setError("Failed to load facts. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    loadFacts();
  }, [user?.uid]);

  const handleFactDelete = async (factKey: string) => {
    try {
      const token = await getIdToken();
      if (!token) return;

      const result = await apiClient.deleteFact(factKey, token);
      if (result.success) {
        setFacts((prev) => {
          if (!prev) return prev;
          const updated = { ...prev };
          delete updated[factKey];
          return updated;
        });
      } else {
        logger.error("Failed to delete fact:", result.message);
        showToast("Failed to delete fact");
      }
    } catch (err) {
      logger.error("Failed to delete fact:", err);
      showToast("Failed to delete fact");
    }
  };

  const factsArray = Object.entries(facts ?? {})
    .map(([key, value]) => ({ key, ...value }))
    .sort((a, b) => a.key.localeCompare(b.key));

  return (
    <ScreenLayout>
      <ScrollView>
        <Title subtitle="These are persistent facts about you">Facts</Title>

        {loading ? (
          <Spinner style={styles.loadingContainer} />
        ) : error ? (
          <View style={styles.errorContainer}>
            <NotificationCard variant="error" label="Facts" message={error} />
          </View>
        ) : factsArray.length === 0 ? (
          <View style={styles.emptyContainer}>
            <Text size="small" color="tertiary" style={styles.emptyText}>
              No facts yet. As you chat, {brand.name} learns about you.
            </Text>
          </View>
        ) : (
          <View style={styles.listContent}>
            {factsArray.map((item, index) => (
              <React.Fragment key={item.key}>
                {index > 0 && <View style={styles.separator} />}
                <FactCard
                  label={item.key}
                  value={item.value}
                  onDelete={() => handleFactDelete(item.key)}
                />
              </React.Fragment>
            ))}
          </View>
        )}
      </ScrollView>
      <DisclaimerFooter />
    </ScreenLayout>
  );
}

const styles = StyleSheet.create({
  loadingContainer: {
    paddingVertical: layout.space.xl
  },
  errorContainer: {
    alignItems: "center",
    paddingVertical: layout.space.xl
  },
  emptyContainer: {
    alignItems: "center",
    paddingVertical: layout.space.xl,
    gap: layout.space.sm
  },
  emptyText: {
    textAlign: "center",
    fontStyle: "italic"
  },
  listContent: {
    paddingBottom: layout.space.xl
  },
  separator: {
    height: layout.space.md
  }
});
